/* eslint-disable camelcase */
import type { ChartData, ChartOptions } from 'chart.js';
import moment, { Moment } from 'moment';
import React, { useState, useEffect } from 'react';

import { Doughnut } from 'react-chartjs-2';
import swal from 'sweetalert';

import DateSelectRange, { getNameByRange } from '../../../../elements/DateSelectRange';
import Loader from '../../../../elements/Loader';
import QuestionMark from '../../../../elements/common/QuestionMark';
import { TimeRangeName } from '../../../../helpers/ShowMetrics';

import 'chartjs-plugin-doughnutlabel';

const currencyCode = process.env.MIX_CURRENCY;

type DoughnutType = 'current' | 'previous';
type UnitType = 'amount' | 'percentage';

interface Props {
    title: string;
    questionMarkText: string;
    axiosRequestCallback: (formattedStartDate: string, formattedEndDate: string) => Promise<any>;
    unit: UnitType;
}

export default function DoughnutBlock({ title, questionMarkText, axiosRequestCallback, unit }: Props) {
    const [currentValue, setCurrentValue] = useState<number>(0);
    const [previousValue, setPreviousValue] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<Boolean>(true);

    const [range, setRange] = useState<TimeRangeName>('this-month');
    const [startDate, setStartDate] = useState<Moment>(moment().startOf('month'));
    const [endDate, setEndDate] = useState<Moment>(moment().endOf('month'));
    const [previousStartDate, setPreviousStartDate] = useState<Moment>(moment().startOf('month').subtract(1, 'month'));
    const [previousEndDate, setPreviousEndDate] = useState<Moment>(moment().endOf('month').subtract(1, 'month'));

    const [previousRange, setPreviousRange] = useState<TimeRangeName>('last-month');

    function updatePreviousDates(newRange: TimeRangeName) {
        setPreviousRange(newRange);
        let start = moment();
        let end = moment();

        switch (newRange) {
            case 'yesterday':
                start = start.subtract(1, 'days').startOf('day');
                end = end.subtract(1, 'days').endOf('day');
                break;
            case 'last-week':
                start = start.subtract(1, 'weeks').startOf('isoWeek');
                end = end.subtract(1, 'weeks').endOf('isoWeek');
                break;
            case 'last-month':
                start = start.subtract(1, 'months').startOf('month');
                end = end.subtract(1, 'months').endOf('month');
                break;
            default:
                return;
        }

        setPreviousStartDate(start);
        setPreviousEndDate(end);
    }

    function triggerUpdate() {
        const formattedStartDate = startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss');
        const formattedEndDate = endDate?.endOf('day').format('YYYY-MM-DD HH:mm:ss');

        setIsLoading(true);
        axiosRequestCallback(formattedStartDate, formattedEndDate).then((response) => {
            setCurrentValue(response.data);
        });
        updatePreviousDates(getPreviousRange());
        setIsLoading(false);
    }

    useEffect(() => {
        triggerUpdate();
    }, []);

    useEffect(() => {
        triggerUpdate();
    }, [startDate, endDate]);

    useEffect(() => {
        const formattedStartDate = previousStartDate.startOf('day').format('YYYY-MM-DD HH:mm:ss');
        const formattedEndDate = previousEndDate?.endOf('day').format('YYYY-MM-DD HH:mm:ss');

        axiosRequestCallback(formattedStartDate, formattedEndDate)
            .then((response) => {
                setPreviousValue(response.data);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'We have encountred an error.',
                    icon: 'error',
                });
            });
    }, [previousStartDate, previousEndDate]);

    function getPreviousRange() {
        switch (range) {
            case 'today':
                return 'yesterday';
            case 'this-week':
                return 'last-week';
            case 'this-month':
                return 'last-month';
            default:
                return null;
        }
    }

    function getDoughnut(type: DoughnutType = 'current') {
        const isCurrent = type === 'current';
        const currentRangeName = getNameByRange(range);
        const previousRangeName = getNameByRange(previousRange);

        const labels = isCurrent ? [currentRangeName, previousRangeName] : [previousRangeName, currentRangeName];
        const values = isCurrent ? [currentValue, previousValue] : [previousValue, currentValue];
        const value = isCurrent ? currentValue : previousValue;
        let innerText: string | number = '';
        if (unit === 'amount') {
            innerText = value > 1000 ? `${value / 1000}K` : value ?? 0;
            innerText += ` ${currencyCode}`;
        }
        if (unit === 'percentage') {
            innerText = `${value * 100 ?? 0} %`;
        }

        const data: ChartData = {
            labels,
            datasets: [
                {
                    data: values,
                    backgroundColor: ['rgb(255,192,14)', 'rgb(81,81,81)'],
                    borderColor: 'rgba(0, 0, 0, 0)',
                },
            ],
        };

        const options: ChartOptions = {
            responsive: true,
            legend: {
                display: false,
            },
            title: {
                display: true,
                fontSize: 20,
                text: isCurrent ? currentRangeName : previousRangeName,
                position: 'bottom',
                fontColor: '#fff',
            },
            plugins: {
                doughnutlabel: {
                    labels: [
                        {
                            text: innerText,
                            font: {
                                size: '18',
                                weight: 'bold',
                            },
                            color: '#fff',
                            position: 'center',
                            padding: 50,
                        },
                    ],
                },
            },
        };

        return <Doughnut data={data} options={options} />;
    }

    const getContent = () => {
        return (
            <div className="dashboard-block">
                <div className="dashboard-block-header">
                    <div className="dashboard-block-header-title">
                        {title} <QuestionMark definition={questionMarkText} />
                    </div>
                    <div className="dashboard-block-date-filter">
                        <DateSelectRange
                            range={range}
                            setRange={setRange}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            displayRanges={['today', 'this-week', 'this-month']}
                        />
                    </div>
                </div>
                <div className="dashboard-block-data">
                    <div className="dashboard-block-data-previous">{getDoughnut('previous')}</div>
                    <div className="dashboard-block-data-current">{getDoughnut()}</div>
                </div>
            </div>
        );
    };
    return <Loader loading={isLoading} content={getContent} />;
}
